import Vue from 'vue';
import { decode, htmlDecode } from 'utils/general';
import bus from 'lib/bus';
import { mapActions } from 'vuex';
import store from 'store';
import { trackEvent } from '../../assets/js/lib/tracking';

function extractURLParams(url) {
  const queryString = url.split('?')[1] || '';
  const queryParams = queryString.split('&');
  const utmParams = {};
  queryParams.forEach(queryParam => {
    const [key, value] = queryParam.split('=');
    utmParams[key] = value;
  });

  return utmParams;
}

export default (el) =>
  new Vue({
    el,
    store,
    components: {
      'vue-image': () => import('../vue-image/vue-image.vue'),
      'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
    },
    data() {
      const cookies = document.cookie.split('; ').reduce((prev, current) => {
        const [name, ...value] = current.split('=');
        prev[name] = value.join('=');
        return prev;
      }, {});
      return {
        availableCountries: [],
        currentCountry: {
          name: 'United States',
          flag: '',
          iso_code: 'US',
        },
        originCountry: {
          name: 'United States',
          flag: '',
          iso_code: 'US',
        },
        currentLang: {
          name: 'English',
          iso_code: 'en',
        },
        originLang: {
          name: 'English',
          iso_code: 'en',
        },
        availableLanguages: [],
        isPanelOpen: false,
        cookies,
        isLoadingFlag: false
      };
    },
    beforeMount() {
      const countries = this.$el.dataset.countries;
      const languages = this.$el.dataset.languages;
      const countryObj = this.$el.dataset.currentCountry
        ? JSON.parse(decode(this.$el.dataset.currentCountry))
        : null;
      const defaultISOCode = this.$el.dataset.defaultIsoCode;
      if (countries) {
        this.availableCountries = JSON.parse(decode(countries));
        const selectedCountry = this.availableCountries.find(
          (country) => country.iso_code === Shopify.country,
        );
        // push UK manually when using metaobjects instead of markets
        if (!selectedCountry && countryObj && defaultISOCode === 'GB') {
          this.availableCountries.push(countryObj);
        }
        // sort countries alphabetically
        this.availableCountries.sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          return 1;
        });
      }
      if (languages) {
        this.availableLanguages = JSON.parse(decode(languages));
      }
      this.originCountry = this.availableCountries.find(
        (country) => country.iso_code === Shopify.country,
      );
      this.originLang = this.availableLanguages.find(
        (language) => language.iso_code === Shopify.locale,
      );
      this.currentCountry = { ...this.originCountry };
      this.currentLang = { ...this.originLang };
    },
    mounted() {
      bus.$on('show-country-selector-panel', () => {
        this.isPanelOpen = true;
        this.$el.parentElement.classList.add('is-opened');
        setTimeout(() => {
          this.$refs.countryClose.focus();
        }, 500);
      });
      this.handleKeyboardClose();
      this.showGeolocationModal();
    },
    methods: {
      ...mapActions('global', ['openModal']),

      async onCountryChange(country) {
        this.isLoadingFlag = true;
        this.currentCountry = country;
        
        const loadImage = (src) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => reject();
            img.src = src;
          });
        };

        const minDelay = new Promise(resolve => setTimeout(resolve, 800));

        try {
          await Promise.all([
            loadImage(country.flag),
            minDelay
          ]);
        } catch (error) {
          console.error('Failed to load flag image:', error);
          await minDelay;
        } finally {
          this.isLoadingFlag = false;
        }
      },

      onLangChange(lang) {
        this.currentLang = lang;
      },
      onSaveSelection() {
        const geoParam = {
          geo: 1
        };
        const params = new URLSearchParams(window.location.search);
        const utmParam = params.toString().includes('utm') ? extractURLParams(window.location.href) : {};

        const finalParams = {
          ...utmParam,
          ...geoParam
        };

        const geoString = `?${new URLSearchParams(finalParams).toString()}`;

        if (
          this.originCountry.iso_code != this.currentCountry.iso_code ||
          this.originLang.iso_code != this.currentLang.iso_code
        ) {
          let expire = new Date(Date.now() + 300 * 864e5).toUTCString();
          document.cookie = `geo_selected=1; expires=${expire}; path=/`;

          trackEvent({
            event: 'shipping_info_change',
            country: this.currentCountry.name,
            language: this.currentLang.name,
            currency_code: this.currentCountry.currency
          })

          if (this.currentCountry?.link !== '') {
            window.location = this.currentCountry.link + geoString;
          } else {
            this.$refs.localization.submit();
          }
        }
      },
      decodeName(name) {
        return htmlDecode(name);
      },
      onPanelClose() {
        this.currentCountry = { ...this.originCountry };
        this.isPanelOpen = false;
        this.$el.parentElement.classList.remove('is-opened');
      },
      handleKeyboardClose(e) {
        document.addEventListener('keyup', (e) => {
          if (e.keyCode === 27 && this.isPanelOpen) {
            this.onPanelClose();
            document.querySelector('.header__logo .header__logo-link')?.focus();
          }
        });
      },
      async showGeolocationModal() {
        const params = new URLSearchParams(window.location.search);
        const shopifyLocationResponse = await fetch(
          // eslint-disable-next-line max-len
          `${window.Shopify.routes.root}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${window.Shopify.country}&language[enabled]=true&language[exclude]=${window.Shopify.language}`,
        ).then((r) => r.json());
        const geoData = {
          country: {
            name: shopifyLocationResponse?.detected_values?.country?.name,
            iso_code: shopifyLocationResponse?.detected_values?.country?.handle,
          },
          language:
            shopifyLocationResponse?.suggestions?.[0]?.parts?.language?.handle,
        };
        this.$store.commit('global/setMarketRecommendation', geoData);
        const countryExists = this.availableCountries.find(
          (country) => country.iso_code === geoData.country.iso_code,
        );
        if (params.has('geo')) {
          let expire = new Date(Date.now() + 300 * 864e5).toUTCString();
          document.cookie = `geo_selected=1; expires=${expire}; path=/`;
        } else if (
          geoData.country.iso_code !== this.currentCountry.iso_code &&
          !this.cookies?.geo_selected &&
          countryExists
        ) {
          let expire = new Date(Date.now() + 10 * 864e5).toUTCString();
          document.cookie = `geo_data=${JSON.stringify(
            geoData,
          )}; expires=${expire}; path=/`;
          this.openModal('geolocation');
        }
      },
    },
  });
